import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Layout, Typography, Button, Divider, Card, Row, Col, List } from 'antd';
import { EditOutlined, ShopOutlined, UserAddOutlined, EnvironmentOutlined } from '@ant-design/icons';

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;

// Mapping von Slug zu korrektem Namen inkl. Umlaute
const bundeslandNameMapping = {
    'baden-wuerttemberg': 'Baden-Württemberg',
    'bayern': 'Bayern',
    'berlin': 'Berlin',
    'brandenburg': 'Brandenburg',
    'bremen': 'Bremen',
    'hamburg': 'Hamburg',
    'hessen': 'Hessen',
    'mecklenburg-vorpommern': 'Mecklenburg-Vorpommern',
    'niedersachsen': 'Niedersachsen',
    'nordrhein-westfalen': 'Nordrhein-Westfalen',
    'rheinland-pfalz': 'Rheinland-Pfalz',
    'saarland': 'Saarland',
    'sachsen': 'Sachsen',
    'sachsen-anhalt': 'Sachsen-Anhalt',
    'schleswig-holstein': 'Schleswig-Holstein',
    'thueringen': 'Thüringen',
};

// NEU: Größte Städte pro Bundesland (Beispiele)
const staedteProBundesland = {
    'baden-wuerttemberg': ['Stuttgart', 'Mannheim', 'Karlsruhe', 'Freiburg'],
    'bayern': ['München', 'Nürnberg', 'Augsburg', 'Regensburg'],
    'berlin': ['Berlin'], // Berlin ist ein Stadtstaat
    'brandenburg': ['Potsdam', 'Cottbus', 'Brandenburg an der Havel'],
    'bremen': ['Bremen', 'Bremerhaven'], // Bremen ist ein Stadtstaat
    'hamburg': ['Hamburg'], // Hamburg ist ein Stadtstaat
    'hessen': ['Frankfurt am Main', 'Wiesbaden', 'Kassel', 'Darmstadt'],
    'mecklenburg-vorpommern': ['Rostock', 'Schwerin', 'Neubrandenburg'],
    'niedersachsen': ['Hannover', 'Braunschweig', 'Osnabrück', 'Oldenburg'],
    'nordrhein-westfalen': ['Köln', 'Düsseldorf', 'Dortmund', 'Essen', 'Bielefeld'],
    'rheinland-pfalz': ['Mainz', 'Ludwigshafen', 'Koblenz', 'Trier'],
    'saarland': ['Saarbrücken', 'Neunkirchen', 'Homburg'],
    'sachsen': ['Leipzig', 'Dresden', 'Chemnitz'],
    'sachsen-anhalt': ['Halle (Saale)', 'Magdeburg', 'Dessau-Roßlau'],
    'schleswig-holstein': ['Kiel', 'Lübeck', 'Flensburg'],
    'thueringen': ['Erfurt', 'Jena', 'Gera'],
};

// Angepasste Hilfsfunktion, die das Mapping verwendet
function formatBundeslandName(slug) {
    return bundeslandNameMapping[slug] || // Versuche, aus Mapping zu lesen
           (slug ? slug.split('-') // Fallback: Standardformatierung
                       .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                       .join(' ') : '');
}

function BundeslandPage() {
    const { bundeslandSlug } = useParams();
    const bundeslandName = formatBundeslandName(bundeslandSlug);
    const staedte = staedteProBundesland[bundeslandSlug] || []; // Städte für das aktuelle Bundesland holen

    // SEO Titel und Beschreibung
    const pageTitle = `Snus & Nikotinbeutel Shops in ${bundeslandName} | Snus Finder Deutschland`;
    const pageDescription = `Finde Kioske, Tankstellen und Shops für Snus und Nikotinbeutel in ${bundeslandName}. Entdecke Verkaufsstellen in deiner Nähe oder melde einen neuen Laden.`;

    // SEO Verbesserung: Strukturierte Daten (Beispiel für Breadcrumbs)
    const breadcrumbJsonLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [{
          "@type": "ListItem",
          "position": 1,
          "name": "Snus Finder Deutschland",
          "item": "https://www.snus-finder.de" // TODO: Domain anpassen
        },{
          "@type": "ListItem",
          "position": 2,
          "name": `Shops in ${bundeslandName}`
        }]
      };

    return (
        <Content style={{ padding: '2rem 1rem', background: '#f0f2f5', minHeight: 'calc(100vh - 140px)' }}>
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <link rel="canonical" href={`https://www.snus-finder.de/bl/${bundeslandSlug}`} />
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbJsonLd)}
                </script>
            </Helmet>

            <div style={{ maxWidth: '900px', margin: '0 auto', background: '#fff', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>

                <Title level={1} style={{ textAlign: 'center', marginBottom: '1rem' }}>
                    <ShopOutlined style={{ marginRight: '10px', color: 'var(--primary-color)' }} />
                    Snus & Nikotinbeutel in {bundeslandName}
                </Title>
                <Paragraph style={{ textAlign: 'center', fontSize: '1.1rem', color: '#555', marginBottom: '2rem' }}>
                    Entdecke Verkaufsstellen für Nikotinbeutel und Snus in <Text strong>{bundeslandName}</Text>. Unser Verzeichnis wächst täglich – hilf mit, es zu vervollständigen!
                </Paragraph>

                {/* NEU: Abschnitt für Städte-Links */}
                {staedte.length > 0 && (
                    <Card title={`Shops in großen Städten von ${bundeslandName} finden`} bordered={false} style={{ marginBottom: '2rem' }} headStyle={{fontSize: '1.2rem'}}>
                         <Paragraph style={{ marginBottom: '1rem' }}>
                            Suche direkt in den größten Städten des Bundeslandes nach Snus und Nikotinbeutel Verkaufsstellen:
                        </Paragraph>
                        <List
                            grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 4 }} // Responsive Grid
                            dataSource={staedte}
                            renderItem={stadt => (
                                <List.Item>
                                    <Link to={`/shops?location=${encodeURIComponent(stadt)}`} style={{ display: 'block', textAlign: 'center' }}>
                                        <Button type="link" icon={<EnvironmentOutlined />} style={{ width: '100%', whiteSpace: 'normal', height: 'auto', padding: '5px' }}>
                                            {stadt}
                                        </Button>
                                    </Link>
                                </List.Item>
                            )}
                        />
                    </Card>
                )}

                <Title level={2} style={{ textAlign: 'center', marginBottom: '1.5rem' }}>Verzeichnis erweitern</Title>

                <Row gutter={[16, 16]} justify="center">
                    <Col xs={24} md={12}>
                        <Card hoverable>
                            <Title level={3}><EditOutlined style={{ marginRight: '8px' }} /> Shop melden (kostenlos)</Title>
                            <Paragraph>
                                Du kennst einen Kiosk, eine Tankstelle oder einen anderen Laden in {bundeslandName}, der Snus oder Nikotinbeutel verkauft? Teile dein Wissen mit der Community!
                            </Paragraph>
                            <Link to="/shop-melden">
                                <Button type="default" size="large" icon={<EditOutlined />} block>
                                    Shop in {bundeslandName} melden
                                </Button>
                            </Link>
                        </Card>
                    </Col>

                    <Col xs={24} md={12}>
                        <Card hoverable>
                            <Title level={3}><UserAddOutlined style={{ marginRight: '8px' }} /> Shop registrieren (Besitzer)</Title>
                            <Paragraph>
                                Bist du der Besitzer eines Shops? Registriere deinen Laden offiziell, verwalte deine Daten und wähle optional einen Premium-Plan für mehr Sichtbarkeit.
                            </Paragraph>
                            <Link to="/register">
                                <Button type="primary" size="large" icon={<UserAddOutlined />} block>
                                    Meinen Shop registrieren
                                </Button>
                            </Link>
                        </Card>
                    </Col>
                </Row>

                <Divider style={{ margin: '3rem 0' }} />

                <div>
                    <Title level={3}>Warum Snus-Finder.de für {bundeslandName}?</Title>
                    <Paragraph>
                        Die Suche nach Snus und Nikotinbeuteln kann mühsam sein. Snus-Finder.de zielt darauf ab, die Suche in {bundeslandName} und ganz Deutschland zu vereinfachen. Egal ob in Großstädten oder ländlichen Regionen – wir möchten dir helfen, schnell und einfach herauszufinden, wo du deine bevorzugten Pouches kaufen kannst. Mit deiner Hilfe bauen wir das umfassendste Verzeichnis auf.
                    </Paragraph>
                </div>

            </div>
        </Content>
    );
}

export default BundeslandPage; 